import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  IDropshippingSettings,
  IMPORT_LIST,
  IOrdersList,
  IOrdersParams,
  ImportListParams,
  ILiveProductsParams,
  ORDERS,
  PRODUCTS,
  DROPSHIPPING_SETTINGS,
  MATCH_PRODUCTS,
  BULK_CHECKOUT_SUMMARY,
  ORDER_SHIPPING_ADDRESS,
  CATEGORIES,
  LIVE_PRODUCTS,
} from "./types";
import {
  createSampleOrder,
  getCategories,
  getImportList,
  getOrders,
  getProducts,
  getDropshippingSettings,
  updateDropshippingSettings,
  updateImportListBulk,
  getAliexpressProducts,
  getAliexpressMatchProducts,
  getBulkCheckoutSummary,
  cancelSubOrder,
  getAliexpressProductsById,
  importAliexpressProduct,
  placeAliexpressOrder,
  getOrderShippingAddress,
  getCachedProducts,
  getLiveProducts,
  getCachedWinningProducts,
} from "./requests";
import {
  IAliexpressMatchProductParams,
  IAliexpressProductParams,
  ICategories,
  IProduct,
  IProductList,
  IProductParams,
  ILiveProduct,
} from "~/types/dropshipping";

export const useGetProducts = (params?: IProductParams) =>
  useInfiniteQuery<IProductList>(
    [...PRODUCTS, params],
    ({ pageParam = 1 }) => getProducts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetCachedProducts = (category_id: string, enabled: boolean) =>
  useQuery<IProduct[]>(
    ["", category_id],
    () => getCachedProducts(category_id),
    { enabled: enabled && !!category_id }
  );

export const useGetCachedWinningProducts = ({
  enabled,
}: {
  enabled?: boolean;
}) =>
  useQuery<IProduct[]>(
    [...PRODUCTS, "Winning"],
    () => getCachedWinningProducts(),
    { enabled }
  );

export const useGetAliexpressProducts = (params?: IAliexpressProductParams) =>
  useInfiniteQuery<IProductList>(
    [...PRODUCTS, params],
    ({ pageParam = 1 }) =>
      getAliexpressProducts({ ...params, page: pageParam }),
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetAliexpressMatchProducts = (
  params?: IAliexpressMatchProductParams,
  handleError?: (message: string) => void
) =>
  useInfiniteQuery<IProductList>(
    [...MATCH_PRODUCTS, params],
    async ({ pageParam = 1 }) => {
      try {
        if (!params?.product_id && !params?.image) {
          return Promise.resolve({ page: 1, total_pages: 1, products: [] });
        }
        const response = await getAliexpressMatchProducts({
          ...params,
          page: pageParam,
        });

        if (response?.error) {
          throw new Error(
            response.error.message || "Unknown API error occurred"
          );
        }

        return response;
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred";

        if (handleError) {
          handleError(errorMessage);
        }

        throw error;
      }
    },
    {
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.page + 1;
        if (nextPage <= lastPage.total_pages) return nextPage;
      },
    }
  );

export const useGetAliexpressProductsById = (product_id: string) =>
  useQuery([], () => getAliexpressProductsById(product_id));

export const useGetCategories = () =>
  useQuery<Array<ICategories>>([CATEGORIES], () => getCategories());

export const useGetOrders = (params?: IOrdersParams) =>
  useQuery<IOrdersList>([...ORDERS, params], () => getOrders(params), {
    staleTime: 30 * 1000, // 30s — orders update frequently
  });

export const useGetImportList = (
  params?: ImportListParams,
  options?: { enabled?: boolean }
) =>
  useQuery<IProductList>(
    [...IMPORT_LIST, params],
    () => getImportList(params),
    {
      // Allow callers to defer this query until critical data has loaded
      enabled: options?.enabled !== undefined ? options.enabled : true,
    }
  );

export function useGetLiveProducts(params?: ILiveProductsParams) {
  return useQuery<{ data: ILiveProduct[]; page: number; total_pages: number }>(
    [...LIVE_PRODUCTS, params],
    () => getLiveProducts(params),
    { staleTime: 60 * 1000 } // 1 min — primary data for the page
  );
}

export const useCreateSampleOrder = () => useMutation(createSampleOrder);

export const useDropshippingSettings = () =>
  useQuery<IDropshippingSettings>(DROPSHIPPING_SETTINGS, () =>
    getDropshippingSettings()
  );

export const useUpdateDropshippingSettings = () =>
  useMutation(updateDropshippingSettings);

export const useCancelSubOrder = () => useMutation(cancelSubOrder);

export const useGetBulkCheckoutSummary = (
  subOrderIds: number[] = [],
  orderType: string
) =>
  useQuery([...BULK_CHECKOUT_SUMMARY, orderType, ...subOrderIds], () =>
    getBulkCheckoutSummary(subOrderIds, orderType)
  );

export const useUpdateImportListBulk = () => useMutation(updateImportListBulk);

export const useImportAliexpressProduct = () =>
  useMutation(importAliexpressProduct);

export const usePlaceAliexpressOrder = () => useMutation(placeAliexpressOrder);

export const useOrderShippingAddress = (order_id: number) =>
  useQuery([ORDER_SHIPPING_ADDRESS, order_id], () =>
    getOrderShippingAddress(order_id)
  );
